import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import Sidebar from "../sidebar";
import Navbar from "../navbar";

const ReservationDetails = () => {
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const formatTime = (time) => {
    const hours = Math.floor(time);
    const minutes = (time % 1) * 60;
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/reservation/${id}`);
        setReservation(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch reservation details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex bg-gray-50">
        <div className="fixed lg:relative lg:flex">
          <Sidebar />
        </div>
        <div className="flex-1">
          <Navbar />
          <div className="flex-1 ml-0 lg:ml-64 p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-600">Loading...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="min-h-screen flex bg-gray-50">
        <div className="fixed lg:relative lg:flex">
          <Sidebar />
        </div>
        <div className="flex-1">
          <Navbar />
          <div className="flex-1 ml-0 lg:ml-64 p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-red-600">{error || 'Reservation not found'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      <div className="fixed lg:relative lg:flex">
        <Sidebar />
      </div>
      <div className="flex-1">
        <Navbar />
        <div className="flex-1 ml-0 lg:ml-64 p-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-3xl font-bold text-gray-900">Reservation Details</h2>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Back to List
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{reservation.name}</h3>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    reservation.arrived === "arrived" ? "bg-green-100 text-green-800" :
                    reservation.arrived === "noshow" ? "bg-red-100 text-red-800" :
                    "bg-blue-100 text-blue-800"
                  }`}>
                    {reservation.arrived === "arrived" ? "Arrived" :
                     reservation.arrived === "noshow" ? "No Show" :
                     "Pending"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Time</h4>
                    <p className="mt-1 text-sm text-gray-900">{formatTime(reservation.time)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Number of People</h4>
                    <p className="mt-1 text-sm text-gray-900">{reservation.people}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Zone</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {reservation.zone === 'rooftop' ? 'Rooftop' : 'Salon'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date</h4>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(reservation.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-500">Contact Information</h4>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-gray-900">Phone: {reservation.phone}</p>
                    <p className="text-sm text-gray-900">Email: {reservation.email}</p>
                  </div>
                </div>

                {reservation.comments && (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-500">Comments</h4>
                    <p className="mt-2 text-sm text-gray-900">{reservation.comments}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetails;